import axios from 'axios';
import createAuthRefreshInterceptor from "axios-auth-refresh";
import i18n from "@/locales/vue-i18n";
import { useAuthStore } from '@/stores/auth';
import { useLoadingStore } from '@/stores/loading';

function getAccessToken() {
    return localStorage.getItem('token');
}

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const publicApi = axios.create({
    baseURL,
    headers: {
        'Accept-Language': i18n.global.locale.value
    }
});

const authApi = axios.create({ baseURL });

export const refreshAuthLogic = (failedRequest: any) => {
    const refreshToken = localStorage.getItem('refreshToken');
    const authStore = useAuthStore();

    if (!refreshToken) {
        authStore.logout();
        return Promise.reject(failedRequest);
    }

    return publicApi.get('/student/auth/refresh', {
        headers: {
            'Authorization': `Bearer ${refreshToken}`,
            // Do not show global loader for silent refresh
            'X-No-Loader': 'true'
        }
    }).then((tokenRefreshResponse) => {
        const newAccessToken = tokenRefreshResponse.data.accessToken;
        const newRefreshToken = tokenRefreshResponse.data.refreshToken;

        localStorage.setItem('token', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        if (failedRequest.response.config.headers) {
            failedRequest.response.config.headers['Authorization'] = 'Bearer ' + newAccessToken;
        }

        return Promise.resolve();
    }).catch((error) => {
        console.error("Refresh token failed:", error);
        authStore.logout();
        return Promise.reject(error);
    });
};

// Helper to manage starting/stopping the global loader and remembering per-request state
function withGlobalLoaderStart(config: any) {
    try {
        const headers = config.headers || {};
        if (String(headers['X-No-Loader']).toLowerCase() === 'true') {
            config.__loaderStarted = false;
            return config;
        }
        const loadingStore = useLoadingStore();
        const method = (config.method || 'get').toString().toUpperCase();
        const scope = method === 'GET' ? 'load' : 'save';
        loadingStore.start(scope);
        config.__loaderStarted = true;
        config.__loaderScope = scope;
    } catch {
        // In case Pinia is not yet active, fail silently
        config.__loaderStarted = false;
    }
    return config;
}

function withGlobalLoaderStop(responseOrError: any) {
    try {
        const config = responseOrError?.config || responseOrError?.response?.config || responseOrError?.request?.config;
        if (config && config.__loaderStarted) {
            const loadingStore = useLoadingStore();
            loadingStore.stop(config.__loaderScope);
            config.__loaderStarted = false;
        }
    } catch {
        // ignore
    }
}

// Attach interceptors to both public and auth APIs
[publicApi, authApi].forEach((api) => {
    api.interceptors.request.use((request: any) => {
        return withGlobalLoaderStart(request);
    }, (error) => {
        withGlobalLoaderStop(error);
        return Promise.reject(error);
    });

    api.interceptors.response.use((response) => {
        withGlobalLoaderStop(response);
        return response;
    }, (error) => {
        withGlobalLoaderStop(error);
        return Promise.reject(error);
    });
});

authApi.interceptors.request.use((request) => {
    if (request.headers) {
        request.headers['Authorization'] = `Bearer ${getAccessToken()}`;
        request.headers['Accept-Language'] = i18n.global.locale.value;
    }
    return request;
});

createAuthRefreshInterceptor(authApi, refreshAuthLogic);

export default authApi;